import type { Mix } from './kit';

export type Section = { name: string; bars: number };

export type SongSpec = {
  id: string;
  title: string;
  genre: string;
  bpm: number;
  swing: number;
  lufs: number;
  form: readonly Section[];
  compose(mix: Mix, g: Grid): Promise<void> | void;
  mix: Mix['o'];
};

export type BarInfo = { bar: number; name: string; index: number; bars: number; last: boolean };

export class Grid {
  readonly beat: number;
  readonly step: number;
  readonly barLength: number;

  constructor(
    readonly bpm: number,
    readonly swing: number,
    readonly form: readonly Section[],
    readonly lead = 0.5,
  ) {
    this.beat = 60 / bpm;
    this.step = this.beat / 4;
    this.barLength = this.beat * 4;
  }

  get totalBars(): number {
    return this.form.reduce((n, s) => n + s.bars, 0);
  }

  get length(): number {
    return this.lead + this.totalBars * this.barLength;
  }

  at(bar: number, step = 0): number {
    const whole = Math.floor(step);
    const swung = whole % 2 === 1 ? ((this.swing - 50) / 50) * this.step : 0;
    return this.lead + bar * this.barLength + step * this.step + swung;
  }

  *bars(): Generator<BarInfo> {
    let bar = 0;
    for (const s of this.form) {
      for (let i = 0; i < s.bars; i++) {
        yield { bar, name: s.name, index: i, bars: s.bars, last: i === s.bars - 1 };
        bar++;
      }
    }
  }

  start(name: string, nth = 0): number {
    let bar = 0;
    let seen = 0;
    for (const s of this.form) {
      if (s.name === name) {
        if (seen === nth) return bar;
        seen++;
      }
      bar += s.bars;
    }
    throw new Error(`no section ${name}#${nth}`);
  }
}

export type Step = { step: number; vel: number };

export function hits(pattern: string): Step[] {
  const out: Step[] = [];
  const chars = pattern.replace(/\s/g, '');
  for (let i = 0; i < chars.length; i++) {
    const c = chars[i];
    if (c === 'X') out.push({ step: i, vel: 1 });
    else if (c === 'x') out.push({ step: i, vel: 0.78 });
    else if (c === 'o') out.push({ step: i, vel: 0.5 });
    else if (c === '-') out.push({ step: i, vel: 0.3 });
  }
  return out;
}

export type Note = [step: number, note: string, len: number, vel?: number];
